import { unsafeCSS, html } from "lit";
import { property, query } from "lit/decorators.js";
import { FormControlMixin } from "../../utilities/mixins/form-control.js";
import { LeafElement } from "../leaf-element.js";
import { ValidationController } from "../../utilities/controllers/validation.js";
import { requiredValidator } from "../../utilities/validators.js";
import "../../utilities/events.js";
import "../../utilities/i18n.js";
import "@shoelace-style/localize";
import "../../utilities/translations/en.js";
const styles = "/*------------------------------------*\\\n    #RESET\n\\*------------------------------------*/\n/**\n * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/\n */\n*,\n::slotted(*),\n*:before,\n*:after {\n  box-sizing: border-box;\n}\n\n:host *,\n:host *::before,\n:host *::after {\n  box-sizing: inherit;\n}\n\n[hidden] {\n  display: none !important;\n}\n\n:host {\n  display: block;\n}\n\n:host(:focus),\n:host(:focus-visible),\n:host(:focus-within) {\n  outline: 0;\n}\n:host(:focus) .leaf-c-checkbox__outer,\n:host(:focus-visible) .leaf-c-checkbox__outer,\n:host(:focus-within) .leaf-c-checkbox__outer {\n  outline: var(--leaf-border-width-button-default) solid var(--leaf-color-border-active-outline);\n  outline-offset: var(--leaf-border-width-button-default);\n}\n\n:host(:disabled) {\n  pointer-events: none;\n}\n:host(:disabled) .leaf-c-checkbox__outer,\n:host(:disabled) .leaf-c-checkbox__inner {\n  background-color: var(--leaf-color-bg-disabled);\n  border-color: var(--leaf-color-border-disabled);\n}\n:host(:disabled) leaf-icon[name=check] {\n  color: var(--leaf-color-content-disabled);\n}\n\n.leaf-c-checkbox__wrapper {\n  align-items: center;\n  display: flex;\n  gap: var(--leaf-spacing-8);\n}\n\n.leaf-c-checkbox__outer,\n.leaf-c-checkbox__inner {\n  align-items: center;\n  display: flex;\n  border: var(--leaf-border-width-default) solid;\n  border-radius: 3px;\n  height: var(--leaf-spacing-24);\n  justify-content: center;\n  width: var(--leaf-spacing-24);\n}\n\n.leaf-c-checkbox__inner {\n  background-color: var(--leaf-color-content-brand);\n}\n\nleaf-icon[name=check] {\n  --icon-height: var(--leaf-spacing-24);\n  color: var(--leaf-color-content-knockout);\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};
var __privateMethod = (obj, member, method) => {
  __accessCheck(obj, member, "access private method");
  return method;
};
var _validation, _initialState, _onClick, onClick_fn, _onKeydown, onKeydown_fn, _onToggle, onToggle_fn;
class LeafCheckbox extends FormControlMixin(LeafElement) {
  constructor() {
    super(...arguments);
    __privateAdd(this, _onClick);
    __privateAdd(this, _onKeydown);
    __privateAdd(this, _onToggle);
    this.internals = this.attachInternals();
    __privateAdd(this, _validation, new ValidationController(this, {
      validators: [requiredValidator],
      nativeTarget: () => this
    }));
    __privateAdd(this, _initialState, false);
  }
  connectedCallback() {
    super.connectedCallback();
    this.internals.role = "checkbox";
    this.setFormControlElement(this);
    __privateGet(this, _validation).validate(this.checked ? this.value : void 0);
    this.addEventListener("click", __privateMethod(this, _onClick, onClick_fn));
    this.addEventListener("keydown", __privateMethod(this, _onKeydown, onKeydown_fn));
  }
  updated(changes) {
    super.updated(changes);
    if (changes.has("checked")) {
      this.internals.ariaChecked = this.checked ? "true" : "false";
      if (this.checked) {
        this.internals.states.add("--checked");
      } else {
        this.internals.states.delete("--checked");
      }
    }
    if (changes.has("checked") || changes.has("value")) {
      this.setFormValue(this.checked ? this.value : void 0);
    }
  }
  formAssociatedCallback() {
    __privateSet(this, _initialState, this.getAttribute("checked") === null ? false : true);
  }
  formResetCallback() {
    this.checked = __privateGet(this, _initialState);
    __privateGet(this, _validation).validate(this.checked ? this.value : void 0);
  }
  render() {
    return html`
      <div @click=${__privateMethod(this, _onToggle, onToggle_fn)} class="leaf-c-checkbox__wrapper">
        <div class="leaf-c-checkbox__outer">
          ${this.checked ? html`<div class="leaf-c-checkbox__inner"><leaf-icon set="system" name="check"></leaf-icon></div>` : ""}
        </div>
        <span class="leaf-c-checkbox__label">
          <slot></slot>
        </span>
      </div>
      ${__privateGet(this, _validation).render()}
    `;
  }
}
_validation = /* @__PURE__ */ new WeakMap();
_initialState = /* @__PURE__ */ new WeakMap();
_onClick = /* @__PURE__ */ new WeakSet();
onClick_fn = function(event) {
  if (event.target === this) {
    this.checkboxWrapper.click();
  }
};
_onKeydown = /* @__PURE__ */ new WeakSet();
onKeydown_fn = function(event) {
  if (this.disabled) {
    return;
  }
  if (["Enter", " "].includes(event.key)) {
    event.preventDefault();
    this.checkboxWrapper.click();
  }
};
_onToggle = /* @__PURE__ */ new WeakSet();
onToggle_fn = function(event) {
  event.preventDefault();
  event.stopPropagation();
  if (this.disabled) {
    return;
  }
  this.checked = !this.checked;
  if (!this.value) {
    this.value = "on";
  } else if (this.value === "on") {
    this.value = "";
  }
  __privateGet(this, _validation).validate(this.checked ? this.value : void 0);
};
LeafCheckbox.styles = unsafeCSS(styles);
LeafCheckbox.formAssociated = true;
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckbox.prototype, "checked", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckbox.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], LeafCheckbox.prototype, "invalid", 2);
__decorateClass([
  property()
], LeafCheckbox.prototype, "value", 2);
__decorateClass([
  query(".leaf-c-checkbox__wrapper")
], LeafCheckbox.prototype, "checkboxWrapper", 2);
export {
  LeafCheckbox
};
//# sourceMappingURL=checkbox.component.js.map
